import { Chip, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import {
  MODALIDADES,
  PAUSA_INMEDIATA_MIN,
  PORCENTAJE_NO_VUELVEN_ALTO,
  RESOLUCION_RAPIDA_MIN,
  TIEMPO_MUERTO_ALTO_MIN,
  minutosLegibles,
  numero,
} from "./informeDisparadorComun";

const formatearDia = (iso) => {
  const [anio, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${anio}`;
};

// Una cifra de la fila del gestor: el título de la columna, el valor tal como se ve en la grilla y, debajo, qué significa.
function Seccion({ titulo, cifra, alerta = false, children }) {
  return (
    <MDBox py={1.5} sx={{ borderBottom: "1px solid #e0e0e0", "&:last-of-type": { borderBottom: 0 } }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="baseline" gap={2} flexWrap="wrap">
        <MDTypography variant="button" fontWeight="bold">
          {titulo}
        </MDTypography>
        <MDTypography variant="button" fontWeight="bold" sx={alerta ? { color: "#b26a00" } : undefined}>
          {cifra}
        </MDTypography>
      </MDBox>
      <MDBox mt={0.5}>{children}</MDBox>
    </MDBox>
  );
}

function Linea({ children }) {
  return (
    <MDTypography variant="caption" color="text" display="block">
      {children}
    </MDTypography>
  );
}

// Explica, con los números de un gestor, qué significa cada columna de su fila del Resumen del Informe de Gestión. Está pensado para
// quien tiene una duda sobre una cifra: acá se ve el detalle de qué cuenta cada una.
function DetalleGestorInforme({ open, onClose, gestor: g, desde, hasta }) {
  if (!g) return null;

  const hayMinimo = g.gestionesMinimasDia > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {g.nombre}
          <Chip size="small" label={MODALIDADES[g.modalidad] ?? "Voluntario"} />
          {!g.activo && <Chip size="small" color="error" label="Desactivado" />}
        </MDBox>
        <MDTypography display="block" variant="caption" color="text" fontWeight="regular">
          Del {formatearDia(desde)} al {formatearDia(hasta)}
        </MDTypography>
      </DialogTitle>
      <DialogContent dividers>
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          Qué significa cada cifra de la fila de este gestor, con sus números del período. Los números en{" "}
          <span style={{ color: "#b26a00", fontWeight: "bold" }}>naranja</span> son señales para mirar con más atención; no son sanciones.
        </MDTypography>

        <Seccion titulo="Propuestas" cifra={numero(g.propuestasAsignadas)}>
          <Linea>Casos que el sistema le asignó en el período.</Linea>
          <Linea>
            <strong>Finalizadas ({numero(g.finalizadas)}):</strong> las que cerró cargando un evento.
          </Linea>
          <Linea>
            <strong>Liberadas ({numero(g.liberadas)}):</strong> las que se le sacaron sin que cargara un evento (se reasignó o se cerró el caso).
          </Linea>
          <Linea>
            <strong>En curso ({numero(g.enCurso)}):</strong> las que siguen abiertas, activas o pausadas.
          </Linea>
        </Seccion>

        <Seccion titulo="Tiempo por propuesta" cifra={`Resolución ${minutosLegibles(g.resolucionPromedioMin)}`}>
          <Linea>
            <strong>Resolución:</strong> el tiempo promedio desde que se le asigna el caso hasta que carga el evento. Cuenta solo las propuestas finalizadas.
          </Linea>
          <Linea>
            <strong>Efectivo ({minutosLegibles(g.efectivoPromedioMin)}):</strong> la resolución sin el tiempo que el caso estuvo pausado.
          </Linea>
          {g.finalizadas === 0 && <Linea>Todavía no finalizó ninguna propuesta en el período, por eso no hay promedio.</Linea>}
        </Seccion>

        <Seccion titulo="Pausas" cifra={`${numero(g.pausas)} · ${minutosLegibles(g.pausaTotalMin)}`} alerta={g.pausasInmediatas > 0}>
          <Linea>Cuántas veces pausó un caso asignado y cuánto tiempo lo tuvo pausado en total.</Linea>
          <Linea>
            <strong>Inmediatas ({numero(g.pausasInmediatas)}):</strong> pausas que hizo en los primeros {PAUSA_INMEDIATA_MIN} minutos de asignado el caso,
            señal de querer evitarlo.
          </Linea>
        </Seccion>

        <Seccion titulo="Tiempo muerto" cifra={minutosLegibles(g.tiempoMuertoTotalMin)} alerta={g.tiempoMuertoTotalMin >= TIEMPO_MUERTO_ALTO_MIN}>
          <Linea>
            El tiempo entre que cerró un caso y recibió el siguiente, el mismo día: lo que estuvo sin caso asignado. El máximo ({minutosLegibles(g.tiempoMuertoMaxMin)})
            es la espera más larga.
          </Linea>
          <Linea>Se marca en naranja desde {TIEMPO_MUERTO_ALTO_MIN} min en total.</Linea>
        </Seccion>

        <Seccion titulo="Gestiones" cifra={numero(g.gestiones)}>
          <Linea>Eventos con estado que cargó en el período. No cuenta el evento inicial que crea el sistema al importar el caso.</Linea>
          <Linea>
            <strong>En propuesta ({numero(g.gestionesEnPropuesta)}):</strong> las que cerraron una propuesta.
          </Linea>
          <Linea>
            <strong>Fuera ({numero(g.gestionesFuera)}):</strong> las demás, por ejemplo las hechas con un caso pausado, con el Disparador apagado o sobre casos que el
            sistema no le asignó.
          </Linea>
        </Seccion>

        <Seccion
          titulo="No vuelven"
          cifra={g.porcentajeNoVuelven === null || g.porcentajeNoVuelven === undefined ? "-" : `${g.porcentajeNoVuelven}%`}
          alerta={(g.porcentajeNoVuelven ?? 0) >= PORCENTAJE_NO_VUELVEN_ALTO}
        >
          {g.gestiones === 0 ? (
            <Linea>No cargó gestiones en el período, por eso no hay porcentaje.</Linea>
          ) : (
            <Linea>
              De sus {numero(g.gestiones)} gestiones, {numero(g.eventosQueNoVuelven)} usaron un tipo de evento que saca el caso de la cola (inubicable, excluido…).
              Si el porcentaje es muy alto puede indicar salidas fáciles; se marca en naranja desde {PORCENTAJE_NO_VUELVEN_ALTO} %.
            </Linea>
          )}
          <Linea>
            <strong>Rápidas ({numero(g.resolucionesRapidas)}):</strong> propuestas finalizadas con menos de {RESOLUCION_RAPIDA_MIN} minuto de trabajo efectivo. Se cuentan
            aparte: no forman parte de los eventos de arriba.
          </Linea>
        </Seccion>

        <Seccion titulo="Días" cifra={`${numero(g.diasConActividad)} con actividad`} alerta={g.diasBajoMinimo > 0}>
          <Linea>
            <strong>Con actividad ({numero(g.diasConActividad)}):</strong> días en que cargó al menos una gestión o recibió una propuesta.
          </Linea>
          <Linea>
            <strong>Bajo el mínimo ({numero(g.diasBajoMinimo)}):</strong>{" "}
            {hayMinimo
              ? `días con actividad en los que hizo menos de ${numero(g.gestionesMinimasDia)} gestiones (${numero(g.gestionesMinimasDia)}/día). El mínimo es el de su usuario o, si no tiene, el de su perfil.`
              : "este gestor no tiene un mínimo de gestiones por día (ni en su usuario ni en su perfil), por eso no se cuentan días bajo el mínimo."}
          </Linea>
          <Linea>
            <strong>Sin actividad ({numero(g.diasSinActividad)}):</strong> días hábiles (lunes a viernes) del período, hasta hoy, sin ninguna gestión ni propuesta. Incluye
            ausencias y feriados.
          </Linea>
        </Seccion>

        <Seccion
          titulo="Horario promedio"
          cifra={g.primerMovimientoPromedio ? `${g.primerMovimientoPromedio} – ${g.ultimoMovimientoPromedio ?? "-"}` : "-"}
        >
          <Linea>Promedio, entre sus días con actividad, de la hora del primer movimiento del día y de la hora del último.</Linea>
          <Linea>
            <strong>Primer movimiento:</strong> lo más temprano entre su primer evento del día y la hora en que le asignaron el primer caso.
          </Linea>
          <Linea>
            <strong>Último movimiento:</strong> lo más tarde entre su último evento del día y el cierre de un caso que le asignaron ese mismo día. Si un caso asignado
            hoy se cierra otro día, hoy cuenta la hora en que se le asignó, y el cierre cuenta en el día en que se cargó el evento.
          </Linea>
        </Seccion>

        <Seccion titulo="Casos hoy" cifra={`${numero(g.casosAbiertos)} abiertos`}>
          <Linea>
            Casos abiertos que tiene asignados hoy; {numero(g.casosSinGestionar)} nunca se gestionaron (solo tienen el evento inicial). No dependen del período elegido.
          </Linea>
        </Seccion>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="info" onClick={onClose}>
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default DetalleGestorInforme;
